import { motion } from "framer-motion";
import { GraduationCap, TrendingUp, Bot, MessageCircle, Globe, Trophy } from "lucide-react";
import { useMemo } from "react";

interface FeaturesSectionProps {
  onCTAClick: () => void;
  className?: string;
}

export default function FeaturesSection({ onCTAClick }: FeaturesSectionProps) {
  const backgroundElements = useMemo(() => {
    /* eslint-disable react-hooks/purity */
    return Array.from({ length: 20 }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      rotate: Math.random() * 360,
      duration: 3 + Math.random() * 2,
      delay: Math.random() * 2,
    }));
    /* eslint-enable react-hooks/purity */
  }, []);

  const features = [
    {
      icon: GraduationCap,
      title: "أكاديمية من الصفر إلى الاحتراف",
      description: "تعلّم بشكل منظم من الأساسيات إلى الاستراتيجيات المتقدمة",
    },
    {
      icon: TrendingUp,
      title: "توصيات تداول يومية جاهزة",
      description: "احصل على توصيات مدروسة من خبراء التداول يومياً",
    },
    {
      icon: Bot,
      title: "روبوت تداول يعمل بالذكاء الاصطناعي",
      description: "استفد من تقنيات الذكاء الاصطناعي في تحليل الأسواق",
    },
    {
      icon: MessageCircle,
      title: "دعم مباشر من المدربين",
      description: "فريق متواجد 24/7 للإجابة على جميع استفساراتك",
    },
    {
      icon: Globe,
      title: "مجتمع خاص للمتداولين",
      description: "انضم لمجتمع نشط من المتداولين وتبادل الخبرات",
    },
    {
      icon: Trophy,
      title: "جوائز مالية لأفضل المشاركين",
      description: "تنافس مع الآخرين واحصل على جوائز مالية قيمة",
    },
  ];

  return (
    <section className="relative py-24 bg-gradient-to-b from-[#000000] to-[#0A1E3F] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        {backgroundElements.map((element, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-20 bg-[#CFAE58]"
            style={{
              left: `${element.left}%`,
              top: `${element.top}%`,
              rotate: `${element.rotate}deg`,
            }}
            animate={{
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: element.duration,
              repeat: Infinity,
              delay: element.delay,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            كل ما تحتاجه لتبدأ التداول <span className="text-[#CFAE58]">بثقة</span> خلال{" "}
            <span className="text-[#CFAE58]">30 يوم فقط</span>
          </h2>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="bg-gradient-to-br from-[#0A1E3F]/50 to-[#000000]/50 backdrop-blur-sm border border-[#CFAE58]/20 rounded-2xl p-8 h-full hover:border-[#CFAE58]/50 transition-all duration-300 hover:shadow-2xl hover:shadow-[#CFAE58]/10 transform hover:scale-105">
                {/* Icon with Glow */}
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#CFAE58] rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                    <div className="relative bg-gradient-to-br from-[#CFAE58] to-[#B89A45] p-5 rounded-full">
                      <feature.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl text-white mb-3 text-center group-hover:text-[#CFAE58] transition-colors">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-center leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-block bg-gradient-to-r from-[#CFAE58]/10 to-transparent border border-[#CFAE58]/30 rounded-full px-8 py-4 mb-8">
            <p className="text-2xl text-[#CFAE58]">
              "إنت بس التزم 30 يوم، وإحنا بنتكفّل بالباقي!"
            </p>
          </div>

<button
  onClick={onCTAClick}
  className="bg-[#CFAE58] text-white px-10 py-4 rounded-xl hover:bg-[#B89A45] transition-all duration-300 
             shadow-[0_0_25px_rgba(207,174,88,0.9),_0_0_50px_rgba(207,174,88,0.6)] 
             transform hover:scale-105 drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]"
>
  سجّل الآن
</button>

        </motion.div>
      </div>
    </section>
  );
}
